import { useEffect, useRef, useState } from 'react';

import { api, ApiError } from '../../../api/client';
import { maskPhone, type RegistrationDraft, type RegistrationRole } from '../model';
import { localizeSmsError } from '../smsError';
import { Button, WizardButton } from '../../../ui';

export interface RegistrationSmsStepProps {
  draft: RegistrationDraft;
  role: RegistrationRole;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
  onSuccess: () => void;
  onChangePhone: () => void;
}

export function CodeInputs({ code, onChange }: { code: string; onChange: (value: string) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  function fillCode(value: string, index: number) {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    if (digits.length > 1) {
      onChange(digits);
      refs.current[Math.min(digits.length, 5)]?.focus();
      return;
    }
    const chars = code.padEnd(6, ' ').split('');
    chars[index] = digits || ' ';
    onChange(chars.join('').trimEnd());
    if (digits && index < 5) refs.current[index + 1]?.focus();
  }
  return (
    <div className="request-wizard__code-row standalone-auth__code-row">
      {Array.from({ length: 6 }, (_, index) => (
        <input
          aria-label={`Цифра ${index + 1}`}
          className={code[index] ? 'is-filled' : ''}
          inputMode="numeric"
          key={index}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={6}
          ref={(node) => { refs.current[index] = node; }}
          value={code[index] ?? ''}
          onChange={(event) => {
            if ((event.nativeEvent as InputEvent).inputType?.startsWith('delete')) {
              onChange('');
              refs.current[0]?.focus();
              return;
            }
            const value = event.target.value;
            // Typing over a filled cell replaces that digit; autofill can supply all six.
            fillCode(value.length === 2 && code[index] && value.startsWith(code[index]) ? value.slice(-1) : value, index);
          }}
          onFocus={(event) => event.target.select()}
          onPaste={(event) => {
            event.preventDefault();
            fillCode(event.clipboardData.getData('text'), index);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
              event.preventDefault();
              onChange('');
              refs.current[0]?.focus();
            } else if (event.key === 'ArrowLeft' && index > 0) {
              event.preventDefault();
              refs.current[index - 1]?.focus();
            } else if (event.key === 'ArrowRight' && index < 5) {
              event.preventDefault();
              refs.current[index + 1]?.focus();
            }
          }}
          onBeforeInput={(event) => {
            if ((event.nativeEvent as InputEvent).inputType?.startsWith('delete')) {
              event.preventDefault();
              onChange('');
              refs.current[0]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
}

export function RegistrationSmsStep({
  draft,
  role,
  updateDraft,
  onSuccess,
  onChangePhone,
}: RegistrationSmsStepProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendSeconds, setResendSeconds] = useState(42);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setTimeout(() => setResendSeconds((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const verify = async () => {
    if (draft.smsCode.replace(/\s/g, '').length !== 6) {
      return setError('Введите шестизначный код.');
    }
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/sms/verify/', {
        method: 'POST',
        body: { phone: draft.phone, purpose: 'register', code: draft.smsCode },
      });
      onSuccess();
    } catch (nextError) {
      setError(localizeSmsError(nextError instanceof ApiError ? nextError.message : 'Неверный код.'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', {
        method: 'POST',
        body: { phone: draft.phone, purpose: 'register' },
      });
      updateDraft({ smsCode: result.debug_code ?? '', debugSmsCode: result.debug_code ?? '' });
      setResendSeconds(42);
    } catch (nextError) {
      setError(nextError instanceof ApiError ? nextError.message : 'Не удалось отправить код.');
    } finally {
      setBusy(false);
    }
  };

  const isCustomer = role === 'customer';

  return (
    <div
      className={isCustomer ? 'standalone-auth__registration-form standalone-auth__registration-form--sms' : 'carrier-registration__form carrier-registration__form--sms'}
      data-figma-node={isCustomer ? '2787:53046' : '2787:50158'}
    >
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Введите код из SMS</h1>
        <p>Отправили на {maskPhone(draft.phone)}. Подтверждаем телефон — email подтвердите потом в профиле.</p>
      </div>
      <CodeInputs code={draft.smsCode} onChange={(smsCode) => updateDraft({ smsCode })} />
      <div className="request-wizard__sms-actions standalone-auth__sms-actions">
        <WizardButton disabled={busy} onClick={() => void verify()}>
          {busy ? 'Проверяем…' : 'Подтвердить'}
        </WizardButton>
        <Button
          aria-disabled={resendSeconds > 0}
          kind="tertiary"
          size="medium"
          onClick={() => {
            if (!busy && resendSeconds === 0) void resend();
          }}
        >
          {resendSeconds > 0
            ? `Не пришёл код? Отправить снова через 0:${String(resendSeconds).padStart(2, '0')}`
            : 'Не пришёл код? Отправить снова'}
        </Button>
        {error ? (
          <div className="form-feedback form-feedback--error" role="alert">
            {error}
          </div>
        ) : null}
        <Button kind="tertiary" size="medium" onClick={onChangePhone}>
          Сменить номер телефона
        </Button>
      </div>
    </div>
  );
}
